import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { MapPin, Shield, Flame, User, Lock, ChevronRight, Loader2, Landmark, Radio, Users, Newspaper, Sparkles, Stamp } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";
import { useMenuAudio } from "@/components/menu/useMenuAudio";

/* JOIN SEQUENCE (CEF) — /cef/join/:serverId?token=<session>. The bridge opens this
   when a player connects. Cinematic entry with a "while you were away" city recap,
   first-join character creation, then spawn point + faction select (Bornwood Sheriff's
   Dept / Bornwood Fire Rescue gated only when the owner enables whitelist_factions).
   Deploy queues a bridge op that spawns the player in-game. Demo floor without token. */

const OUTFITS = ["Streetwear", "Utility", "Business", "Workwear"];
const ACCENTS = ["#2E6BFF", "#f43f5e", "#22d3ee", "#34d399", "#f59e0b", "#a78bfa"];
const FACTION_ICON = { police: Shield, fire: Flame, ems: Flame, civ: User, custom: User };

/* passport photo presets — stylised CSS portraits, no assets */
const PORTRAITS = ["p1", "p2", "p3", "p4", "p5", "p6"];
const PORTRAIT_HUE = { p1: 210, p2: 20, p3: 150, p4: 280, p5: 340, p6: 95 };
function Portrait({ id, className = "w-full" }) {
  const h = PORTRAIT_HUE[id] ?? 210;
  return (
    <div className={`${className} aspect-[4/5] overflow-hidden`} style={{ background: `linear-gradient(160deg, hsl(${h},42%,32%), hsl(${h},48%,15%))` }}>
      <svg viewBox="0 0 40 50" className="w-full h-full">
        <circle cx="20" cy="17" r="8" fill={`hsl(${h},32%,${58 + (h % 3) * 6}%)`} />
        <path d="M5 50 C5 35 35 35 35 50 Z" fill={`hsl(${h},30%,${42 + (h % 4) * 5}%)`} />
        <circle cx="17" cy="15.5" r="1" fill="hsl(0,0%,8%)" /><circle cx="23" cy="15.5" r="1" fill="hsl(0,0%,8%)" />
      </svg>
    </div>
  );
}

const DEMO_BOOT = {
  server: { id: "SRV-OFFICIAL-1", name: "Bornwood Springs RP", mode: "Serious RP" },
  player: { playercode: 37049, session_name: "Guest Citizen" },
  is_new: true, character: null, bank: 5000, faction_id: "citizen", spawn_id: "central",
  age_rating: "16+", rules_agreed: false,
  intro: {
    logo_url: "", tagline: "Serious county RP. Every shift tells a story — write yours.",
    discord: "https://discord.gg/NPhbFVSmpd",
    rules: [
      "Stay in character on the streets — break RP in /ooc only.",
      "No random deathmatch: every hostile act needs a story reason.",
      "Police are players too — comply, flee, or lawyer up, but don't grief them.",
      "Bans are appealed at the courthouse kiosk, not in Discord DMs.",
    ],
    camera: ["pan-harbor-sunrise", "dolly-founders-square"],
  },
  spawns: [
    { id: "central", name: "Bornwood Central Station", desc: "Heart of the city — steps from shops and the courthouse" },
    { id: "hospital", name: "Bornwood General Hospital", desc: "Quiet northside start near the EMS depot" },
    { id: "motel", name: "Route 68 Motel", desc: "Cheap beds on the strip — where fresh starts begin" },
    { id: "harbor", name: "Bornwood Harbor", desc: "Dockside air, container yards and night work" },
  ],
  factions: [
    { id: "citizen", name: "Citizen", type: "civ", blurb: "Forge your own path — work, trade, hustle", locked: false },
    { id: "sheriff", name: "Bornwood Sheriff's Department", type: "police", blurb: "Patrol the county, answer 911 calls, serve the court", locked: false },
    { id: "fire", name: "Bornwood Fire Rescue", type: "fire", blurb: "Fireground response, rescues and EMS support", locked: false },
  ],
  whitelist_factions: false,
};
const DEMO_RECAP = {
  server_name: "Bornwood Springs RP", players_online: 47, mayor: "Ada King",
  announcements: [
    { by: "Mayor Ada King", text: "Sales tax holds at 8% — small business grants open Friday", at: "" },
    { by: "City Hall Wire", text: "Sheriff's Dept hiring night moved to the county courthouse", at: "" },
  ],
  open_calls: 3, recent_verdicts: [{ suspect: "M. Webb", verdict: "Guilty — 18 months" }], away_since: "", citizen_count: 612,
};

export default function JoinCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;
  const audio = useMenuAudio();

  const [phase, setPhase] = useState("gate"); // gate | entry | intro | create | deploy | launching
  const [boot, setBoot] = useState(null);
  const [recap, setRecap] = useState(null);
  const [form, setForm] = useState({ name: "", dob: "", origin: "", outfit: "Streetwear", accent: ACCENTS[0], portrait: "p1" });
  const [rulesOk, setRulesOk] = useState(false);
  const [spawn, setSpawn] = useState("central");
  const [faction, setFaction] = useState("citizen");
  const [busy, setBusy] = useState(false);

  const intro = boot?.intro || {};
  const needsIntro = Boolean(boot?.is_new && !boot?.rules_agreed && (intro.rules?.length || intro.tagline || intro.logo_url));

  const load = useCallback(() => {
    if (demo) {
      // demo floor: no session — but still pull the server's REAL intro/name so the
      // owner-deck "preview join flow" shows what was actually saved
      api.get(`/nc/servers/${serverId}/intro`).then((r) => {
        const i = r.data.intro || {};
        const hasIntro = i.rules?.length || i.tagline || i.logo_url;
        setBoot({ ...DEMO_BOOT,
                  server: { id: serverId, name: r.data.name || DEMO_BOOT.server.name, mode: DEMO_BOOT.server.mode },
                  age_rating: r.data.age_rating || DEMO_BOOT.age_rating,
                  intro: hasIntro ? i : DEMO_BOOT.intro });
      }).catch(() => setBoot(DEMO_BOOT));
      setRecap(DEMO_RECAP);
      return;
    }
    api.get(`/nc/servers/${serverId}/join/bootstrap`, { params: { token } })
      .then((r) => {
        setBoot(r.data);
        setSpawn(r.data.spawn_id || "central");
        setFaction(r.data.faction_id || "citizen");
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get(`/nc/servers/${serverId}/join/recap`, { params: { token } }).then((r) => setRecap(r.data)).catch(() => {});
  }, [demo, serverId, token]);
  useEffect(load, [load]);

  const enter = () => {
    audio.ensure(); audio.boom(); audio.startMusic();
    setPhase("entry");
  };

  const createCharacter = async () => {
    if (form.name.trim().length < 3) { toast.error("Name needs at least 3 characters"); return; }
    if (demo) {
      toast.success(`Welcome to the city, ${form.name}`);
      setBoot((b) => ({ ...b, is_new: false, character: { ...form } }));
      setPhase("deploy");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/join/character`, { ...form, rules_accepted: rulesOk }, { params: { token } });
      toast.success(r.data.message);
      setPhase("deploy");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const deploy = async () => {
    const fac = boot.factions.find((f) => f.id === faction);
    if (fac?.locked) { toast.error(`${fac.name} is whitelisted here — apply through staff first`); return; }
    audio.whoosh?.();
    if (demo) { setPhase("launching"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/join/deploy`, { spawn_id: spawn, faction_id: faction }, { params: { token } });
      toast.success(r.data.message);
      setPhase("launching");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const selFaction = boot?.factions?.find((f) => f.id === faction);
  const selSpawn = boot?.spawns?.find((s) => s.id === spawn);

  return (
    <div data-testid="join-page" className="fixed inset-0 overflow-hidden text-zinc-100 bg-[#050608]" style={{ fontFamily: "'Rajdhani','Segoe UI',sans-serif" }}>
      {demo && <PreviewRibbon what="join sequence" />}

      {/* skyline backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ background: "linear-gradient(#0a1220 0%, #16223a 45%, #0a0d16 70%, #050608 100%)" }} />
        <svg className="absolute bottom-0 w-full" viewBox="0 0 1200 260" preserveAspectRatio="none" style={{ height: "42%", opacity: 0.9 }}>
          <path fill="#0b0f18" d="M0 260 V180 h60 v-40 h40 v40 h50 V120 h60 v60 h45 v-90 h55 v90 h60 V90 h70 v90 h50 v-50 h60 v50 h55 V70 h65 v110 h60 v-60 h50 v60 h60 V100 h70 v80 h55 v-45 h60 v45 h50 v80 Z" />
          <path fill="#070a11" d="M0 260 V210 h80 v-30 h70 v30 h90 V150 h80 v60 h70 v-40 h80 v40 h90 V130 h80 v80 h70 v-35 h80 v35 h90 V160 h80 v50 h70 v50 Z" />
        </svg>
        {Array.from({ length: 40 }, (_, i) => (
          <span key={i} className="absolute w-1 h-1 bg-amber-200/70" style={{ left: `${(i * 37) % 100}%`, bottom: `${8 + ((i * 13) % 30)}%`, opacity: 0.25 + (i % 4) * 0.15, animation: `ncTwinkle ${2 + (i % 5)}s ease-in-out ${i * 0.3}s infinite` }} />
        ))}
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 110%, #2E6BFF22, transparent 60%)" }} />
      </div>

      {/* GATE — audio requires a gesture */}
      {phase === "gate" && (
        <button data-testid="join-gate" onClick={enter} className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-5 cursor-pointer group">
          <p className="font-mono text-[10px] tracking-[0.6em] text-zinc-500 uppercase" style={{ animation: "ncFadeUp 0.8s ease 0.2s both" }}>{boot?.server?.name || "New Citizen"}</p>
          <h1 className="font-black uppercase tracking-[0.12em] text-4xl sm:text-6xl text-center px-6" style={{ animation: "ncFadeUp 0.9s ease 0.45s both", textShadow: "0 0 60px #2E6BFF66" }}>
            {boot?.is_new ? "Welcome to the city" : "Welcome back"}
          </h1>
          <p className="font-mono text-[10px] tracking-[0.45em] uppercase text-zinc-400 group-hover:text-zinc-100 transition-colors" style={{ animation: "ncPulse 1.4s ease-in-out 1.2s infinite" }}>
            Click to enter
          </p>
        </button>
      )}

      {/* ENTRY — while you were away */}
      {phase === "entry" && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6" style={{ animation: "ncFadeUp 0.6s ease both" }}>
          <p className="font-mono text-[10px] tracking-[0.5em] uppercase mb-3" style={{ color: "#2E6BFF", animation: "ncFadeUp 0.7s ease 0.2s both" }}>{recap?.server_name || boot?.server?.name}</p>
          <h2 className="font-black uppercase text-3xl sm:text-5xl tracking-[0.08em] text-center" style={{ animation: "ncFadeUp 0.8s ease 0.45s both" }}>
            {boot?.is_new ? "A living city awaits" : `Welcome back, ${boot?.character?.name || boot?.player?.session_name || "Citizen"}`}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-10 w-full max-w-3xl">
            {[
              [Users, `${recap?.players_online ?? "—"} online now`, "streets are moving"],
              [Landmark, recap?.mayor ? `Mayor ${recap.mayor}` : "Office vacant", "city hall"],
              [Radio, `${recap?.open_calls ?? 0} open 911 calls`, "dispatch board"],
              [Sparkles, `${recap?.citizen_count ?? "—"} citizens`, "and counting"],
            ].map(([Icon, big, small], i) => (
              <div key={i} data-testid={`recap-card-${i}`} className="border border-white/10 bg-black/50 backdrop-blur px-4 py-4 text-center" style={{ animation: `ncFadeUp 0.6s ease ${0.9 + i * 0.18}s both` }}>
                <Icon className="w-4 h-4 mx-auto mb-2" style={{ color: "#2E6BFF" }} />
                <p className="font-bold text-sm leading-tight">{big}</p>
                <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{small}</p>
              </div>
            ))}
          </div>
          {(recap?.announcements || []).length > 0 && (
            <div data-testid="recap-wire" className="mt-6 w-full max-w-3xl border border-white/10 bg-black/50 backdrop-blur px-5 py-4" style={{ animation: "ncFadeUp 0.6s ease 1.7s both" }}>
              <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2"><Newspaper className="w-3 h-3" /> While you were away</p>
              {recap.announcements.slice(0, 3).map((a, i) => (
                <p key={i} className="font-mono text-[11px] text-zinc-300 leading-relaxed py-1 border-b border-white/5 last:border-0">
                  <span style={{ color: "#2E6BFF" }}>{a.by || a.author}:</span> {a.body || a.text}
                </p>
              ))}
            </div>
          )}
          <button data-testid="join-continue" onClick={() => { audio.click(); setPhase(boot?.is_new ? (needsIntro ? "intro" : "create") : "deploy"); }}
            className="mt-10 px-10 py-4 font-mono text-xs uppercase tracking-[0.4em] font-bold text-black flex items-center gap-3 transition-transform hover:scale-105"
            style={{ backgroundColor: "#2E6BFF", boxShadow: "0 0 40px #2E6BFF55", animation: "ncFadeUp 0.6s ease 2.1s both" }}>
            Continue <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* INTRO — passport control: owner branding + house rules */}
      {phase === "intro" && (
        <div data-testid="join-intro" className="absolute inset-0 z-20 flex items-center justify-center px-6" style={{ animation: "ncFadeUp 0.5s ease both" }}>
          <div className="w-full max-w-lg border border-white/12 bg-black/75 backdrop-blur-xl p-7 flex flex-col gap-4 max-h-[90vh]">
            <div className="flex items-center gap-4">
              {intro.logo_url
                ? <img src={intro.logo_url} alt="" className="w-14 h-14 rounded-xl object-cover border border-white/15" />
                : <span className="w-14 h-14 rounded-xl border border-[#2E6BFF]/40 bg-[#2E6BFF]/10 flex items-center justify-center font-black text-xl" style={{ color: "#2E6BFF" }}>{(boot?.server?.name || "N")[0]}</span>}
              <div className="min-w-0">
                <p className="font-mono text-[9px] tracking-[0.4em] uppercase" style={{ color: "#2E6BFF" }}>Passport control</p>
                <h2 className="font-black uppercase tracking-[0.08em] text-xl truncate">{boot?.server?.name}</h2>
                {boot?.age_rating && <span className="inline-block mt-1 font-mono text-[8px] uppercase tracking-[0.2em] border border-amber-400/40 text-amber-300 px-2 py-0.5">Rated {boot.age_rating}</span>}
              </div>
            </div>
            {intro.tagline && <p className="font-serif italic text-sm text-zinc-300 leading-relaxed" style={{ fontFamily: "Georgia, serif" }}>"{intro.tagline}"</p>}
            {intro.discord && (
              <a data-testid="intro-discord" href={intro.discord} target="_blank" rel="noopener noreferrer"
                className="font-mono text-[9px] uppercase tracking-[0.2em] border border-[#5865F2]/50 text-[#8b9dff] hover:bg-[#5865F2]/10 px-3 py-2 w-fit transition-colors flex items-center gap-2">
                <Users className="w-3.5 h-3.5" /> Join the community Discord
              </a>
            )}
            {(intro.rules || []).length > 0 && (
              <div className="border border-white/10 bg-black/40 p-4 max-h-48 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
                <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500 mb-2">House rules — read before registration</p>
                {intro.rules.map((r, i) => (
                  <p key={i} data-testid={`intro-rule-${i}`} className="font-mono text-[10px] text-zinc-300 leading-relaxed py-1 flex gap-2.5">
                    <span className="text-zinc-600 shrink-0">{String(i + 1).padStart(2, "0")}</span>{r}
                  </p>
                ))}
              </div>
            )}
            {(intro.rules || []).length > 0 && (
              <label className="flex items-center gap-2.5 font-mono text-[10px] text-zinc-300 cursor-pointer">
                <input data-testid="intro-agree" type="checkbox" checked={rulesOk} onChange={(e) => setRulesOk(e.target.checked)} className="accent-[#2E6BFF]" />
                I have read and agree to the house rules
              </label>
            )}
            <button data-testid="intro-continue" disabled={(intro.rules || []).length > 0 && !rulesOk}
              onClick={() => { audio.click(); setPhase("create"); }}
              className="py-4 font-mono text-xs uppercase tracking-[0.35em] font-bold text-black disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ backgroundColor: "#2E6BFF" }}>
              Proceed to registration <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* PASSPORT — first-join citizen registration */}
      {phase === "create" && (
        <div data-testid="char-create" className="absolute inset-0 z-20 flex items-center justify-center px-6 overflow-y-auto py-6" style={{ animation: "ncFadeUp 0.5s ease both" }}>
          <div className="w-full max-w-2xl border-2 border-[#c9a84c]/50 bg-[#0d1b2e]/95 backdrop-blur-xl p-7 shadow-[0_0_80px_rgba(201,168,76,0.15)]">
            <div className="flex items-center justify-between border-b border-[#c9a84c]/30 pb-3 mb-5">
              <div>
                <p className="font-mono text-[8px] tracking-[0.5em] uppercase text-[#c9a84c]">City of Bornwood</p>
                <h2 className="font-black uppercase tracking-[0.12em] text-2xl text-zinc-50 mt-1">Citizen Passport</h2>
              </div>
              <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 text-right">registration<br />form C-01</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-6">
              {/* photo booth */}
              <div>
                <span className="font-mono text-[9px] tracking-[0.25em] text-[#c9a84c] uppercase block mb-1.5">Photo</span>
                <div data-testid="char-portrait-preview" className="border border-[#c9a84c]/40 p-1 bg-black/40">
                  <Portrait id={form.portrait} />
                </div>
                <div className="grid grid-cols-6 gap-1 mt-2">
                  {PORTRAITS.map((p) => (
                    <button key={p} data-testid={`char-portrait-${p}`} onClick={() => { audio.tick(); setForm({ ...form, portrait: p }); }}
                      className={`border-2 transition-all ${form.portrait === p ? "border-[#c9a84c] scale-105" : "border-transparent opacity-70 hover:opacity-100"}`}>
                      <Portrait id={p} />
                    </button>
                  ))}
                </div>
              </div>
              {/* fields */}
              <div className="flex flex-col gap-4">
                <div>
                  <span className="font-mono text-[9px] tracking-[0.25em] text-[#c9a84c] uppercase block mb-1.5">Full name</span>
                  <input data-testid="char-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={40}
                    placeholder="e.g. Reyes Martinez" className="w-full bg-black/60 border border-white/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#c9a84c]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="font-mono text-[9px] tracking-[0.25em] text-[#c9a84c] uppercase block mb-1.5">Date of birth</span>
                    <input data-testid="char-dob" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} placeholder="1994-06-21" maxLength={12}
                      className="w-full bg-black/60 border border-white/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#c9a84c]" />
                  </div>
                  <div>
                    <span className="font-mono text-[9px] tracking-[0.25em] text-[#c9a84c] uppercase block mb-1.5">Origin</span>
                    <input data-testid="char-origin" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} placeholder="Bornwood" maxLength={60}
                      className="w-full bg-black/60 border border-white/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#c9a84c]" />
                  </div>
                </div>
                <div>
                  <span className="font-mono text-[9px] tracking-[0.25em] text-[#c9a84c] uppercase block mb-1.5">Arrival style</span>
                  <div className="flex gap-2 flex-wrap">
                    {OUTFITS.map((o) => (
                      <button key={o} data-testid={`char-outfit-${o.toLowerCase()}`} onClick={() => setForm({ ...form, outfit: o })}
                        className={`px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] border transition-colors ${form.outfit === o ? "text-black" : "border-white/15 text-zinc-400"}`}
                        style={form.outfit === o ? { backgroundColor: form.accent, borderColor: form.accent } : {}}>{o}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="font-mono text-[9px] tracking-[0.25em] text-[#c9a84c] uppercase block mb-1.5">Accent</span>
                  <div className="flex gap-2">
                    {ACCENTS.map((c) => (
                      <button key={c} data-testid={`char-accent-${c.slice(1)}`} onClick={() => setForm({ ...form, accent: c })}
                        className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${form.accent === c ? "border-white" : "border-transparent"}`}
                        style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>
                <div className="border-t border-[#c9a84c]/30 pt-3">
                  <span className="font-mono text-[9px] tracking-[0.25em] text-[#c9a84c] uppercase block mb-1">Signature of bearer</span>
                  <p data-testid="char-signature" className="font-serif italic text-2xl text-zinc-100 min-h-8" style={{ fontFamily: "Georgia, serif" }}>
                    {form.name || <span className="text-zinc-600 text-base not-italic font-mono">— sign above with your full name</span>}
                  </p>
                </div>
              </div>
            </div>
            <button data-testid="char-create-btn" disabled={busy || form.name.trim().length < 3} onClick={createCharacter}
              className="mt-6 w-full py-4 font-mono text-xs uppercase tracking-[0.35em] font-bold text-black disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ backgroundColor: "#c9a84c" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Stamp className="w-4 h-4" />} Issue passport
            </button>
          </div>
        </div>
      )}

      {/* DEPLOY — spawn + faction */}
      {phase === "deploy" && boot && (
        <div data-testid="deploy-screen" className="absolute inset-0 z-20 flex items-center justify-center px-6 overflow-y-auto py-8" style={{ animation: "ncFadeUp 0.5s ease both" }}>
          <div className="w-full max-w-4xl">
            <p className="font-mono text-[9px] tracking-[0.4em] uppercase mb-1" style={{ color: "#2E6BFF" }}>{boot.character?.name || boot.player?.session_name}</p>
            <h2 className="font-black uppercase tracking-[0.1em] text-2xl mb-6">Choose your start</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-1 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Spawn point</p>
                {boot.spawns.map((s) => (
                  <button key={s.id} data-testid={`spawn-${s.id}`} onClick={() => { setSpawn(s.id); audio.tick(); }}
                    className={`text-left border px-4 py-3.5 transition-all ${spawn === s.id ? "border-[#2E6BFF] bg-[#2E6BFF0d]" : "border-white/10 bg-black/50 hover:border-white/30"}`}>
                    <p className="font-bold text-sm flex items-center gap-2"><MapPin className="w-3.5 h-3.5" style={{ color: "#2E6BFF" }} /> {s.name}</p>
                    <p className="font-mono text-[9px] text-zinc-500 mt-1">{s.desc}</p>
                  </button>
                ))}
              </div>
              <div className="flex flex-col gap-2">
                <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-1">Faction {boot.whitelist_factions && <span className="text-amber-400">· departments whitelisted on this server</span>}</p>
                {boot.factions.map((f) => {
                  const Icon = FACTION_ICON[f.type] || User;
                  return (
                    <button key={f.id} data-testid={`faction-${f.id}`} disabled={f.locked}
                      onClick={() => { setFaction(f.id); audio.tick(); }}
                      className={`text-left border px-4 py-3.5 transition-all relative ${f.locked ? "border-white/5 bg-black/30 opacity-55 cursor-not-allowed" : faction === f.id ? "border-[#2E6BFF] bg-[#2E6BFF0d]" : "border-white/10 bg-black/50 hover:border-white/30"}`}>
                      <p className="font-bold text-sm flex items-center gap-2">
                        <Icon className="w-4 h-4" style={{ color: f.type === "police" ? "#3b82f6" : f.type === "fire" ? "#f97316" : "#a1a1aa" }} />
                        {f.name}
                        {f.locked && <span className="ml-auto font-mono text-[8px] uppercase tracking-[0.15em] border border-amber-500/40 text-amber-400 px-1.5 py-0.5 flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Whitelisted</span>}
                      </p>
                      <p className="font-mono text-[9px] text-zinc-500 mt-1">{f.blurb}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <button data-testid="deploy-btn" disabled={busy || selFaction?.locked} onClick={deploy}
              className="mt-6 w-full py-4 font-mono text-xs uppercase tracking-[0.4em] font-bold text-black disabled:opacity-40 flex items-center justify-center gap-3 transition-all hover:tracking-[0.5em]"
              style={{ backgroundColor: "#2E6BFF", boxShadow: "0 0 40px #2E6BFF44" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
              Deploy — {selFaction?.name} · {selSpawn?.name}
            </button>
          </div>
        </div>
      )}

      {/* LAUNCHING */}
      {phase === "launching" && (
        <div data-testid="deploy-launching" className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 bg-black/85 backdrop-blur">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#2E6BFF" }} />
          <p className="font-mono text-[11px] tracking-[0.4em] uppercase">Deploying into {selSpawn?.name || "the city"}…</p>
          <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em]">the bridge is placing you in the world</p>
        </div>
      )}

      <style>{`
        @keyframes ncFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
        @keyframes ncPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
        @keyframes ncTwinkle { 0%, 100% { opacity: 0.15; } 50% { opacity: 0.8; } }
      `}</style>
    </div>
  );
}
